import {
	ArrowDropDown,
	KeyboardArrowDown,
	KeyboardArrowRight,
} from "@mui/icons-material";
import {
	Box,
	Checkbox,
	Collapse,
	Divider,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	Popover,
	TextField,
	Typography,
} from "@mui/material";
import { useState } from "react";
import {
	getMeterLabel,
	type UtilityConsumptionMeter,
} from "../models/UtilityConsumption";

interface AccountGroup {
	key: string;
	accountNumber: string;
	accountName: string | null;
	meters: UtilityConsumptionMeter[];
}

const groupByAccount = (meters: UtilityConsumptionMeter[]): AccountGroup[] => {
	const groups = new Map<string, AccountGroup>();
	for (const meter of meters) {
		const key = String(meter.WaterAccountId ?? "none");
		const group = groups.get(key);
		if (group === undefined) {
			groups.set(key, {
				key,
				accountNumber: meter.AccountNumber ?? "No account",
				accountName: meter.AccountName ?? null,
				meters: [meter],
			});
			continue;
		}
		group.meters.push(meter);
	}
	return [...groups.values()];
};

interface ConsumptionMeterTreeProps {
	meters: UtilityConsumptionMeter[];
	isLoading: boolean;
	selectedMeterIds: string[] | null;
	onMeterChange: (meterIds: string[] | null) => void;
}

export const ConsumptionMeterTree = ({
	meters,
	isLoading,
	selectedMeterIds,
	onMeterChange,
}: ConsumptionMeterTreeProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [collapsedAccounts, setCollapsedAccounts] = useState<string[]>([]);

	const accounts = groupByAccount(meters);
	const allMeterIds = meters.map((meter) => meter.ExternalMeterId);
	const isAllSelected =
		selectedMeterIds === null ||
		(allMeterIds.length > 0 && selectedMeterIds.length === allMeterIds.length);
	const selectedIds = new Set(
		isAllSelected ? allMeterIds : (selectedMeterIds ?? []),
	);

	const commit = (nextIds: Set<string>) => {
		const orderedIds = allMeterIds.filter((meterId) => nextIds.has(meterId));
		onMeterChange(orderedIds.length === allMeterIds.length ? null : orderedIds);
	};

	const toggleMeter = (meterId: string) => {
		const nextIds = new Set(selectedIds);
		if (nextIds.has(meterId)) nextIds.delete(meterId);
		else nextIds.add(meterId);
		commit(nextIds);
	};

	const toggleAccount = (account: AccountGroup) => {
		const accountMeterIds = account.meters.map(
			(meter) => meter.ExternalMeterId,
		);
		const isWhole = accountMeterIds.every((meterId) =>
			selectedIds.has(meterId),
		);
		const nextIds = new Set(selectedIds);
		for (const meterId of accountMeterIds) {
			if (isWhole) nextIds.delete(meterId);
			else nextIds.add(meterId);
		}
		commit(nextIds);
	};

	const toggleCollapsed = (accountKey: string) =>
		setCollapsedAccounts((current) =>
			current.includes(accountKey)
				? current.filter((key) => key !== accountKey)
				: [...current, accountKey],
		);

	const wholeAccounts = accounts.filter((account) =>
		account.meters.every((meter) => selectedIds.has(meter.ExternalMeterId)),
	);
	const onlyMeter =
		selectedIds.size === 1
			? meters.find((meter) => selectedIds.has(meter.ExternalMeterId))
			: undefined;
	const onlyAccount =
		wholeAccounts.length === 1 &&
		wholeAccounts[0].meters.length === selectedIds.size
			? wholeAccounts[0]
			: undefined;
	const summary = isAllSelected
		? "All meters"
		: onlyMeter !== undefined
			? getMeterLabel(onlyMeter)
			: onlyAccount !== undefined
				? onlyAccount.accountNumber
				: `${selectedIds.size} meters`;

	return (
		<>
			<TextField
				label="Meters"
				size="small"
				fullWidth
				value={
					meters.length === 0
						? "No meters"
						: selectedIds.size === 0
							? "None"
							: summary
				}
				disabled={isLoading || meters.length === 0}
				onClick={(event) => setAnchorEl(event.currentTarget)}
				InputProps={{
					readOnly: true,
					endAdornment: <ArrowDropDown color="action" />,
					sx: { cursor: "pointer" },
				}}
				inputProps={{ sx: { cursor: "pointer" } }}
				sx={{ minWidth: 190, maxWidth: 320 }}
			/>
			<Popover
				open={anchorEl !== null}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				slotProps={{
					paper: { sx: { maxHeight: 420, minWidth: 300, mt: 0.5 } },
				}}
			>
				<Box
					sx={{
						alignItems: "center",
						display: "flex",
						gap: 0.5,
						pl: 1,
						pr: 1.5,
						py: 0.5,
					}}
				>
					<Checkbox
						size="small"
						checked={isAllSelected}
						indeterminate={!isAllSelected && selectedIds.size > 0}
						onChange={() => onMeterChange(isAllSelected ? [] : null)}
						inputProps={{ "aria-label": "Select all meters" }}
					/>
					<Typography sx={{ fontWeight: 600 }}>Select all</Typography>
					<Typography
						color="text.secondary"
						sx={{ fontSize: "0.75rem", ml: "auto" }}
					>
						{selectedIds.size}/{allMeterIds.length}
					</Typography>
				</Box>
				<Divider />
				<List dense disablePadding sx={{ py: 0.5 }}>
					{accounts.map((account) => {
						const accountMeterIds = account.meters.map(
							(meter) => meter.ExternalMeterId,
						);
						const selectedCount = accountMeterIds.filter((meterId) =>
							selectedIds.has(meterId),
						).length;
						const isCollapsed = collapsedAccounts.includes(account.key);
						return (
							<Box key={account.key}>
								<ListItemButton
									dense
									onClick={() => toggleAccount(account)}
									sx={{ pl: 0.5, pr: 1.5 }}
								>
									<IconButton
										size="small"
										aria-label={
											isCollapsed ? "Expand account" : "Collapse account"
										}
										onClick={(event) => {
											event.stopPropagation();
											toggleCollapsed(account.key);
										}}
									>
										{isCollapsed ? (
											<KeyboardArrowRight fontSize="small" />
										) : (
											<KeyboardArrowDown fontSize="small" />
										)}
									</IconButton>
									<Checkbox
										size="small"
										checked={selectedCount === accountMeterIds.length}
										indeterminate={
											selectedCount > 0 &&
											selectedCount < accountMeterIds.length
										}
										tabIndex={-1}
										disableRipple
									/>
									<ListItemText
										primary={account.accountNumber}
										secondary={
											account.accountName === account.accountNumber
												? null
												: account.accountName
										}
										primaryTypographyProps={{ fontWeight: 500 }}
										secondaryTypographyProps={{ fontSize: "0.7rem" }}
									/>
									<Typography
										color="text.secondary"
										sx={{ fontSize: "0.75rem", pl: 1 }}
									>
										{accountMeterIds.length}
									</Typography>
								</ListItemButton>
								<Collapse in={!isCollapsed} unmountOnExit>
									<Box
										sx={{
											borderLeft: "1px solid",
											borderColor: "divider",
											ml: 3.5,
										}}
									>
										{account.meters.map((meter) => (
											<ListItemButton
												key={meter.ExternalMeterId}
												dense
												onClick={() => toggleMeter(meter.ExternalMeterId)}
												sx={{ pl: 2, pr: 1.5 }}
											>
												<Checkbox
													size="small"
													checked={selectedIds.has(meter.ExternalMeterId)}
													tabIndex={-1}
													disableRipple
												/>
												<ListItemText
													primary={getMeterLabel(meter)}
													secondary={
														meter.SharePercent > 0 && meter.SharePercent < 100
															? `${meter.SharePercent.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`
															: null
													}
													secondaryTypographyProps={{ fontSize: "0.7rem" }}
												/>
											</ListItemButton>
										))}
									</Box>
								</Collapse>
							</Box>
						);
					})}
				</List>
			</Popover>
		</>
	);
};
